import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { resolveBackendUrl } from '../../services/apiConfig';

const normalizePhoneFriendlyUrl = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    if (['localhost', '127.0.0.1', '0.0.0.0'].includes(parsed.hostname)) {
      parsed.hostname = window.location.hostname;
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

export default function AdminQRCodes() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | with_qr | without_qr
  const [generatingId, setGeneratingId] = useState(null);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [previewMember, setPreviewMember] = useState(null);

  const fetchMembers = () => {
    setLoading(true);
    api.get('/accounts/admin/members/?page_size=500')
      .then(res => setMembers(res.data.results || res.data))
      .catch(() => toast.error('Failed to load members'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchMembers(); }, []);

  const filtered = useMemo(() => {
    let list = members;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        (m.full_name || '').toLowerCase().includes(q) ||
        (m.firm_name || '').toLowerCase().includes(q) ||
        (m.membership_number || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q)
      );
    }
    if (filter === 'with_qr') list = list.filter(m => m.qr_code);
    if (filter === 'without_qr') list = list.filter(m => !m.qr_code);
    return list;
  }, [members, search, filter]);

  const generateForMember = async (id) => {
    setGeneratingId(id);
    try {
      const res = await api.post(`/qr/generate/${id}/`, { frontend_base_url: window.location.origin });
      toast.success(res.data.detail);
      // Update the member in state with qr_code and profile_url
      setMembers(prev => prev.map(m =>
        m.id === id ? { ...m, qr_code: res.data.qr_code_url, profile_url: res.data.profile_url } : m
      ));
    } catch {
      toast.error('Failed to generate QR code.');
    } finally {
      setGeneratingId(null);
    }
  };

  const bulkGenerate = async () => {
    setBulkGenerating(true);
    try {
      const res = await api.post('/qr/bulk-generate/', { frontend_base_url: window.location.origin });
      toast.success(res.data.detail);
      fetchMembers();
    } catch {
      toast.error('Failed to bulk generate QR codes.');
    } finally {
      setBulkGenerating(false);
    }
  };

  const qrUrl = (m) => {
    if (!m.qr_code) return null;
    if (m.qr_code.startsWith('http')) return normalizePhoneFriendlyUrl(m.qr_code);
    return normalizePhoneFriendlyUrl(resolveBackendUrl(m.qr_code));
  };

  const verifyUrl = (m) => {
    // Prefer the profile_url returned from the generate endpoint;
    // otherwise build it using the current frontend origin so phones can open it.
    if (m.profile_url) return normalizePhoneFriendlyUrl(m.profile_url);
    if (m.public_profile_slug) return `${window.location.origin}/verify/${m.public_profile_slug}/`;
    return null;
  };

  const withQR = members.filter(m => m.qr_code).length;
  const withoutQR = members.filter(m => !m.qr_code).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
            QR Code Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Generate and manage member verification QR codes</p>
        </div>
        <button
          onClick={bulkGenerate}
          disabled={bulkGenerating}
          className="bg-[#0B5D1E] text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-[#062E10] transition disabled:opacity-50 flex items-center gap-2 text-sm"
        >
          {bulkGenerating ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Generating...</>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              Generate for All Active Members
            </>
          )}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{members.length}</p>
              <p className="text-xs text-gray-500">Total Members</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">{withQR}</p>
              <p className="text-xs text-gray-500">QR Generated</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{withoutQR}</p>
              <p className="text-xs text-gray-500">Pending</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input
              type="text"
              placeholder="Search by name, firm, membership number..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E]/20 focus:border-[#0B5D1E] outline-none"
            />
          </div>
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0B5D1E]/20 focus:border-[#0B5D1E] outline-none bg-white"
          >
            <option value="all">All Members</option>
            <option value="with_qr">With QR Code</option>
            <option value="without_qr">Without QR Code</option>
          </select>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#0B5D1E]/20 border-t-[#0B5D1E]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            <p className="text-sm">No members found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Member</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600 hidden md:table-cell">Membership No</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600 hidden lg:table-cell">Firm</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-600">QR Status</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(m => (
                  <tr key={m.id} className="hover:bg-gray-50 transition">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {m.photo ? (
                          <img src={m.photo} alt="" className="w-9 h-9 rounded-full object-cover border" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-[#0B5D1E]/10 flex items-center justify-center text-[#0B5D1E] font-bold text-sm">
                            {(m.full_name || '?')[0]}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{m.full_name}</p>
                          <p className="text-xs text-gray-500">{m.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600 hidden md:table-cell">{m.membership_number || '—'}</td>
                    <td className="py-3 px-4 text-gray-600 hidden lg:table-cell">{m.firm_name || '—'}</td>
                    <td className="py-3 px-4 text-center">
                      {m.qr_code ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          Generated
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => generateForMember(m.id)}
                          disabled={generatingId === m.id}
                          className="text-[#0B5D1E] hover:bg-[#0B5D1E]/10 p-2 rounded-lg transition disabled:opacity-50"
                          title={m.qr_code ? 'Regenerate QR' : 'Generate QR'}
                        >
                          {generatingId === m.id ? (
                            <div className="w-4 h-4 border-2 border-[#0B5D1E]/30 border-t-[#0B5D1E] rounded-full animate-spin" />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                          )}
                        </button>
                        {m.qr_code && (
                          <button
                            onClick={() => setPreviewMember(m)}
                            className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition"
                            title="Preview QR"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* QR Preview Modal */}
      {previewMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreviewMember(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-[#0B5D1E] px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">QR Code Preview</h3>
                <p className="text-white/70 text-xs">{previewMember.full_name}</p>
              </div>
              <button onClick={() => setPreviewMember(null)} className="text-white/80 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 text-center space-y-4">
              <div className="bg-gray-50 inline-block p-3 rounded-xl">
                <a
                  href={verifyUrl(previewMember) || qrUrl(previewMember)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <img src={qrUrl(previewMember)} alt="QR Code" className="w-40 h-40" />
                </a>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p><span className="font-semibold">Name:</span> {previewMember.full_name}</p>
                <p><span className="font-semibold">Membership:</span> {previewMember.membership_number || '—'}</p>
                <p><span className="font-semibold">Status:</span> <span className={previewMember.membership_status === 'active' ? 'text-green-600 font-semibold' : 'text-red-600'}>{previewMember.membership_status}</span></p>
              </div>
              {verifyUrl(previewMember) && (
                <>
                  <a
                    href={verifyUrl(previewMember)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#0B5D1E] hover:text-[#062E10] underline text-sm font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                    Open Verification Page
                  </a>
                  <p className="text-xs text-gray-400 break-all">{verifyUrl(previewMember)}</p>
                </>
              )}
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <a
                  href={qrUrl(previewMember)}
                  download={`QR_${previewMember.membership_number || previewMember.full_name}.png`}
                  className="inline-flex items-center gap-2 bg-[#0B5D1E] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#062E10] transition text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  Download QR Code
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
